import React from 'react';
import {
  Box,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { MenuItemWithRole, isModifiedClick } from './navConfig';
import { NAVY, motion } from './shellTokens';

interface RailItemProps {
  item: MenuItemWithRole;
  /** Rail is showing labels. When false the row is an icon-only target. */
  expanded: boolean;
  /** This item's submenu is open. Only honoured while `expanded`. */
  open: boolean;
  active: boolean;
  activeSubPath: string;
  reducedMotion: boolean;
  onToggle: (text: string) => void;
  onNavigate: (path: string) => void;
  /** Live count rendered as a pill (approvals queue). 0/undefined = nothing. */
  badge?: number;
}

/**
 * One top-level rail row, plus its sub-routes.
 *
 * The collapsed state is the important one: the icon must never disappear, so
 * the row keeps a FIXED icon box and collapses only the text to zero width.
 * Hiding the label with `display:none` was rejected — it removes the node from
 * the flex flow mid-transition and the icon visibly jumps left.
 */
const RailItem: React.FC<RailItemProps> = ({
  item,
  expanded,
  open,
  active,
  activeSubPath,
  reducedMotion,
  onToggle,
  onNavigate,
  badge,
}) => {
  const hasSubItems = !!item.subItems && item.subItems.length > 0;

  // Label text, zero-width when collapsed. Shared by the row and (below) the
  // chevron, so both fade on the same curve.
  const labelSx = {
    flex: expanded ? '1 1 auto' : '0 0 0px',
    opacity: expanded ? 1 : 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    m: 0,
    transition: motion(reducedMotion, 'opacity .16s ease .03s'),
  } as const;

  return (
    <>
      <ListItem disablePadding sx={{ mb: 0.25 }}>
        {/* Collapsed rows have no visible name, so the tooltip IS the label.
            An empty title renders no tooltip at all, which is what we want once
            the rail is open and the text is already on screen. */}
        <Tooltip
          title={expanded ? '' : item.text}
          placement="right"
          enterDelay={250}
          slotProps={{
            tooltip: {
              sx: { backgroundColor: '#1E293B', fontSize: '0.75rem', fontWeight: 500 },
            },
          }}
        >
          <ListItemButton
            {...(hasSubItems ? {} : ({ component: RouterLink, to: item.path } as any))}
            onClick={(e: React.MouseEvent) => {
              if (hasSubItems) {
                onToggle(item.text);
              } else {
                // Leaf item is an <a href>: let modified/middle clicks open a
                // new tab natively instead of in-app navigating.
                if (isModifiedClick(e)) return;
                onNavigate(item.path);
              }
            }}
            aria-expanded={hasSubItems ? open && expanded : undefined}
            aria-current={!hasSubItems && active ? 'page' : undefined}
            sx={{
              position: 'relative',
              borderRadius: 2,
              minHeight: 42,
              py: 0.9,
              // Constant left padding in both states: the icon column is the
              // one thing that must not move while the rail animates.
              pl: 1.75,
              pr: expanded ? 1.25 : 1.75,
              color: active ? NAVY.text : NAVY.textMuted,
              backgroundColor: active ? NAVY.active : 'transparent',
              transition: motion(reducedMotion, 'background-color .18s ease, color .18s ease'),
              '&:focus-visible': {
                outline: `2px solid ${NAVY.focus}`,
                outlineOffset: '-2px',
              },
              // Active rail: a gradient bar pinned to the left edge reads as
              // "you are here" at a glance in a way a flat tint does not, and
              // it survives the collapsed state where the label is gone.
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 3,
                height: active ? '58%' : 0,
                borderRadius: '0 3px 3px 0',
                background: NAVY.accentGradient,
                transition: motion(reducedMotion, 'height .2s cubic-bezier(.16,1,.3,1)'),
              },
              '&:hover': {
                backgroundColor: active ? NAVY.active : NAVY.hover,
                color: NAVY.text,
              },
            }}
          >
            <ListItemIcon
              sx={{
                // 38px fixed: 1.75*8 left padding + 38 lands the glyph's optical
                // centre on the 76px rail's centre line.
                minWidth: 38,
                color: active ? NAVY.accent : 'inherit',
                transition: motion(reducedMotion, 'color .18s ease'),
                '& .MuiSvgIcon-root': { fontSize: '1.25rem' },
              }}
            >
              {item.icon}
            </ListItemIcon>

            <ListItemText
              primary={item.text}
              sx={labelSx}
              primaryTypographyProps={{
                fontWeight: active ? 600 : 500,
                fontSize: '0.875rem',
                letterSpacing: '-0.01em',
              }}
            />

            {/* Count pill. Collapsed, it shrinks to a dot on the icon corner so
                the rail still signals "something needs you". */}
            {!!badge && badge > 0 && (
              <Box
                aria-hidden="true"
                sx={
                  expanded
                    ? {
                        flexShrink: 0,
                        ml: 0.5,
                        px: 0.75,
                        py: 0.125,
                        borderRadius: 999,
                        fontSize: '0.6875rem',
                        fontWeight: 700,
                        lineHeight: 1.6,
                        color: '#fff',
                        backgroundColor: '#DC2626',
                        fontVariantNumeric: 'tabular-nums',
                      }
                    : {
                        position: 'absolute',
                        top: 7,
                        left: 30,
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: '#DC2626',
                        border: `2px solid ${NAVY.bg}`,
                      }
                }
              >
                {expanded ? (badge > 99 ? '99+' : badge) : ''}
              </Box>
            )}

            {hasSubItems && (
              <ExpandMore
                sx={{
                  ...labelSx,
                  flex: '0 0 auto',
                  width: expanded ? 18 : 0,
                  fontSize: '1.1rem',
                  opacity: expanded ? 0.55 : 0,
                  transform: open && expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: motion(reducedMotion, 'transform .2s ease, opacity .16s ease'),
                }}
              />
            )}
          </ListItemButton>
        </Tooltip>
      </ListItem>

      {hasSubItems && (
        // Children are suppressed entirely while the rail is collapsed: there
        // is no room for 18 sub-labels at 76px, and rendering them icon-only
        // would produce a second, meaningless icon column.
        <Collapse in={open && expanded} timeout={reducedMotion ? 0 : 'auto'} unmountOnExit>
          {/* Connector spine: ties the children to their parent so an
              eighteen-item submenu doesn't read as eighteen more top-level rows. */}
          <List
            component="div"
            disablePadding
            sx={{
              position: 'relative',
              ml: 3.25,
              pl: 1.5,
              my: 0.5,
              borderLeft: `1px solid ${NAVY.border}`,
            }}
          >
            {item.subItems!.map((subItem, subIdx) => {
              const subActive = activeSubPath === subItem.path;
              const prevGroup = subIdx > 0 ? item.subItems![subIdx - 1].group : undefined;
              const showGroupHeader = !!subItem.group && subItem.group !== prevGroup;
              return (
                <React.Fragment key={subItem.text}>
                  {showGroupHeader && (
                    <ListItem
                      disablePadding
                      sx={{ display: 'block', mt: subIdx === 0 ? 0 : 1, mb: 0.25, pl: 1.5 }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.625rem',
                          fontWeight: 700,
                          letterSpacing: '0.09em',
                          textTransform: 'uppercase',
                          color: NAVY.textMuted,
                        }}
                      >
                        {subItem.group}
                      </Typography>
                    </ListItem>
                  )}
                  <ListItem disablePadding>
                    <ListItemButton
                      component={RouterLink}
                      to={subItem.path}
                      aria-current={subActive ? 'page' : undefined}
                      onClick={(e: React.MouseEvent) => {
                        if (isModifiedClick(e)) return;
                        onNavigate(subItem.path);
                      }}
                      sx={{
                        position: 'relative',
                        py: 0.65,
                        pl: 1.5,
                        pr: 1.5,
                        borderRadius: 1.5,
                        color: subActive ? NAVY.text : NAVY.textMuted,
                        backgroundColor: subActive ? NAVY.active : 'transparent',
                        transition: motion(reducedMotion, 'background-color .18s ease, color .18s ease'),
                        '&:focus-visible': {
                          outline: `2px solid ${NAVY.focus}`,
                          outlineOffset: '-2px',
                        },
                        // Node on the spine, so the active child is legible even
                        // when scrolled away from its parent.
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: -13,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: 5,
                          height: 5,
                          borderRadius: '50%',
                          backgroundColor: subActive ? NAVY.accent : 'transparent',
                          boxShadow: subActive ? `0 0 6px ${NAVY.accent}` : 'none',
                          transition: motion(reducedMotion, 'background-color .18s ease'),
                        },
                        '&:hover': {
                          backgroundColor: subActive ? NAVY.active : NAVY.hover,
                          color: NAVY.text,
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 30,
                          color: subActive ? NAVY.accent : 'inherit',
                          transition: motion(reducedMotion, 'color .18s ease'),
                          '& .MuiSvgIcon-root': { fontSize: '1rem' },
                        }}
                      >
                        {subItem.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={subItem.text}
                        primaryTypographyProps={{
                          fontSize: '0.8125rem',
                          fontWeight: subActive ? 600 : 400,
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        </Collapse>
      )}
    </>
  );
};

export default RailItem;
